import { Dispatch, PayloadAction, createSlice } from '@reduxjs/toolkit';
import { AxiosError, AxiosResponse } from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import {
  Country,
  CountryAttachment,
  CountryPeopleListItem,
  CountryPetsListItem,
  CountryScope,
  GetCountriesParams,
  PaginatedResponse,
  Pagination,
  UpdateCountrySectionDto,
  typicodeApi,
} from 'shared/api';

type TCountryState = {
  list: Country[];
  countriesPeopleList: CountryPeopleListItem[];
  countriesPetsList: CountryPetsListItem[];
  paginationPeople: Pagination | null;
  paginationPets: Pagination | null;
};

const initialState: TCountryState = {
  list: [],
  countriesPeopleList: [],
  countriesPetsList: [],
  paginationPeople: null,
  paginationPets: null,
};

export const countryModel = createSlice({
  name: 'countries',
  initialState,
  reducers: {
    setCountries: (state, { payload }: PayloadAction<Country[]>) => {
      state.list = payload;
    },
    setCountriesPeopleList: (state, { payload }: PayloadAction<CountryPeopleListItem[]>) => {
      state.countriesPeopleList = payload;
    },
    setCountriesPetsList: (state, { payload }: PayloadAction<CountryPetsListItem[]>) => {
      state.countriesPetsList = payload;
    },
    setPaginationPeople: (state, { payload }: PayloadAction<Pagination>) => {
      state.paginationPeople = payload;
    },
    setPaginationPets: (state, { payload }: PayloadAction<Pagination>) => {
      state.paginationPets = payload;
    },
  },
});

// react-query actions (everything that async)
const COUNTRIES_ALL_QUERY_KEY = 'countries-all';
export const COUNTRIES_PEOPLE_ALL_QUERY_KEY = 'countries-people-all';
export const COUNTRIES_PETS_ALL_QUERY_KEY = 'countries-pets-all';
export const COUNTRIES_NEEDS_CHECK_COUNT_QUERY_KEY = 'countries-needs-check-count';

/**
 * Хук для получения полного списка стран с длительным кешированием.
 * Используется в диалогах редактирования для селекторов стран.
 * Данные кешируются на 1 час, так как список стран меняется редко.
 */
export const useAllCountries = () =>
  useQuery<AxiosResponse<Country[]>>([COUNTRIES_ALL_QUERY_KEY], () => typicodeApi.country.getCountries(), {
    staleTime: 60 * 60 * 1000, // 1 час - данные считаются свежими
    cacheTime: 2 * 60 * 60 * 1000, // 2 часа - хранение в кеше
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

export const getCountriesPeopleAllAsync = (params: GetCountriesParams) => (dispatch: Dispatch) =>
  useQuery<AxiosResponse<PaginatedResponse<CountryPeopleListItem>>>(
    [COUNTRIES_PEOPLE_ALL_QUERY_KEY, params],
    () => typicodeApi.country.getCountriesPeople(params),
    {
      onSuccess: ({ data }) => {
        dispatch(countryModel.actions.setCountriesPeopleList(data.data));
        dispatch(countryModel.actions.setPaginationPeople(data.pagination));
        // Также заполняем list для обратной совместимости (для диалогов)
        dispatch(
          countryModel.actions.setCountries(
            data.data.map((item) => ({
              code: item.countryCode,
              name: item.countryName,
            })),
          ),
        );
      },
      onError: () => {
        toast.error('There was an error loading the list of countries, please try reloading the page!');
      },
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

export const getCountriesPetsAllAsync = (params: GetCountriesParams) => (dispatch: Dispatch) =>
  useQuery<AxiosResponse<PaginatedResponse<CountryPetsListItem>>>(
    [COUNTRIES_PETS_ALL_QUERY_KEY, params],
    () => typicodeApi.country.getCountriesPets(params),
    {
      onSuccess: ({ data }) => {
        dispatch(countryModel.actions.setCountriesPetsList(data.data));
        dispatch(countryModel.actions.setPaginationPets(data.pagination));
        // Также заполняем list для обратной совместимости (для диалогов)
        dispatch(
          countryModel.actions.setCountries(
            data.data.map((item) => ({
              code: item.countryCode,
              name: item.countryName,
            })),
          ),
        );
      },
      onError: () => {
        toast.error('There was an error loading the list of countries, please try reloading the page!');
      },
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

/**
 * Количество стран, которым пора перепроверить нацкалендарь.
 * Бек не отдаёт счётчик отдельно, поэтому берём total из пагинации
 * самым лёгким запросом: одна запись на страницу.
 */
export const useCountriesNeedsCheckCount = (scope: CountryScope) =>
  useQuery<AxiosResponse<PaginatedResponse<CountryPeopleListItem | CountryPetsListItem>>, unknown, number>(
    [COUNTRIES_NEEDS_CHECK_COUNT_QUERY_KEY, scope],
    () =>
      scope === 'people'
        ? typicodeApi.country.getCountriesPeople({ needsCheck: true, page: 1, limit: 1 })
        : typicodeApi.country.getCountriesPets({ needsCheck: true, page: 1, limit: 1 }),
    {
      select: ({ data }) => data.pagination.total,
      refetchOnWindowFocus: false,
    },
  );

export type SaveCountryInfoArgs = {
  code: string;
  dto: UpdateCountrySectionDto;
  fileToUpload: File | null;
  attachmentIdToRemove: string | null;
  /**
   * Вызывается сразу после успешного POST, до DELETE/PATCH.
   * Позволяет диалогу сбросить pendingFile, даже если следующий шаг цепочки упадёт —
   * иначе повторный Save отправит тот же файл ещё раз и на беке появится дубликат.
   * Аргумент — вложение из ответа бека (или null, если бек его не вернул).
   */
  onUploaded?: (attachment: CountryAttachment | null) => void;
  /**
   * Вызывается сразу после успешного DELETE, до PATCH.
   * Позволяет диалогу забыть про удалённое вложение, чтобы повторный Save
   * не слал DELETE тем же fileId ещё раз.
   */
  onDeleted?: () => void;
};

/** Разбирает ответ сервера в понятное пользователю сообщение */
const getCountryInfoErrorMessage = (error: unknown): string => {
  const status = (error as AxiosError)?.response?.status;

  if (status === 403) return 'You do not have permission to edit country info';
  if (status === 404) return 'Country not found. The list will be refreshed';
  if (status === 400) return 'Server rejected the data. Check links and the attached file';

  return 'Error saving country info. Try again!';
};

/** true, если ошибка мутации — 404 (страна не найдена, например удалена другим админом) */
export const isCountryNotFoundError = (error: unknown): boolean => (error as AxiosError)?.response?.status === 404;

/**
 * Сохранение паспорта страны одной операцией.
 *
 * Загрузка идёт раньше удаления намеренно: если POST упадёт,
 * старое вложение останется на месте и данные не потеряются.
 * PATCH выполняется последним, чтобы его ответ был финальным состоянием.
 *
 * Список и счётчик инвалидируются в onSettled при ЛЮБОМ исходе — даже если цепочка
 * прервалась на середине (например, POST прошёл, а PATCH упал), UI должен отражать
 * реальное состояние сервера, а не то, что было до Save.
 */
export const useSaveCountryInfo = (scope: CountryScope) => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ code, dto, fileToUpload, attachmentIdToRemove, onUploaded, onDeleted }: SaveCountryInfoArgs) => {
      if (fileToUpload) {
        const { data } = await typicodeApi.country.uploadCountryAttachment(scope, code, fileToUpload);
        const attachments = data.info?.attachments ?? [];
        // В момент ответа POST старое вложение (если оно помечено к удалению) ещё
        // не удалено на беке — в attachments лежат оба, и порядок элементов не гарантирован.
        // Поэтому сначала исключаем по id вложение, помеченное к удалению, и только если
        // это не даёт однозначного результата — берём последний оставшийся элемент.
        const candidates = attachmentIdToRemove
          ? attachments.filter((attachment) => attachment.id !== attachmentIdToRemove)
          : attachments;
        const uploaded = candidates.length === 1 ? candidates[0] : candidates[candidates.length - 1] ?? null;

        onUploaded?.(uploaded);
      }

      if (attachmentIdToRemove) {
        await typicodeApi.country.deleteCountryAttachment(scope, code, attachmentIdToRemove);
        onDeleted?.();
      }

      return typicodeApi.country.updateCountrySection(scope, code, dto);
    },
    {
      onSuccess: () => {
        toast.success('Country info saved successfully!');
      },
      onError: (error: unknown) => {
        toast.error(getCountryInfoErrorMessage(error));
      },
      onSettled: () => {
        void queryClient.invalidateQueries([
          scope === 'people' ? COUNTRIES_PEOPLE_ALL_QUERY_KEY : COUNTRIES_PETS_ALL_QUERY_KEY,
        ]);
        void queryClient.invalidateQueries([COUNTRIES_NEEDS_CHECK_COUNT_QUERY_KEY, scope]);
      },
    },
  );
};

// selectors
export const useCountries = () => useSelector((state: RootState) => state.countries);
export const useCountriesPeopleList = () =>
  useSelector((state: RootState) => ({
    countriesList: state.countries.countriesPeopleList,
    pagination: state.countries.paginationPeople,
  }));
export const useCountriesPetsList = () =>
  useSelector((state: RootState) => ({
    countriesList: state.countries.countriesPetsList,
    pagination: state.countries.paginationPets,
  }));

export const reducer = countryModel.reducer;
